/**
 * Client-side turn arbiter for OpenAI Realtime (LUM-172, hardened
 * across four human smoke calls).
 *
 * The session runs with BOTH server response behaviours disabled:
 *
 *   turn_detection: { create_response: false, interrupt_response: false }
 *
 * so the server never cancels a response on speech onset AND never
 * auto-responds to a committed utterance. The client is the sole turn
 * arbiter (backchanneling + numWords, then beyond it):
 *
 *   - utterance while assistant speaking, NOT an interruption →
 *     backchannel: do NOTHING (audio keeps playing)
 *   - utterance while assistant speaking that IS an interruption →
 *     cancel generation if running, clear buffered playback, respond
 *   - utterance while assistant silent → respond, with pacing decided
 *     by what was said
 *
 * Interruption requires CONTENT, not just words (call 4 finding: the
 * STT renders backchannel hums as multilingual fragments — "Čo? Mhm.",
 * "Ja.", "음" — so a bare word-count of 2 truncated the greeting):
 * ≥ INTERRUPTION_MIN_WORDS words, or any stop-word ("stop", "wait",
 * "sorry", "question"…).
 *
 * Reply pacing is COMPLETENESS-AWARE (call 4 finding: "don't talk over
 * me" — a mid-thought pause was answered): the transcription text
 * decides the debounce. Terminal punctuation → fast reply; a trailing
 * fragment or conjunction → a long grace window; a bare backchannel
 * ("Mm-hmm.", "Okay.") → the longest hold, because it is usually the
 * prelude to the real answer, not the answer (call ws_f9e79c03).
 * Either way, resumed speech cancels the pending reply — so the grace
 * window costs nothing when the user really was done and keeps the
 * floor when they weren't.
 *
 * All reply decisions therefore WAIT for transcription (~400ms after
 * commit); `transcription.failed` falls back to the default delay.
 *
 * Pure state machine, no I/O — the service feeds it data-channel events
 * and executes the returned actions; the Tier-2 audio harness mirrors
 * it over WebSocket against the live API.
 */

/**
 * Turn-policy build stamp, sent at connect and recorded on the call's
 * registry row — an open SPA tab keeps running pre-deploy code, and
 * calls made from stale bundles are otherwise indistinguishable from
 * current-code failures. Bump on every behavioural change here.
 */
export const CLIENT_TURN_POLICY_BUILD = 'arbiter-v16-bch-hold-1';

/** An utterance with this many words interrupts regardless of content. */
export const INTERRUPTION_MIN_WORDS = 4;
/** Reply delay after a complete-sounding utterance (terminal punctuation). */
export const RESPONSE_DELAY_COMPLETE_MS = 300;

/** Reply delay when transcription text is unavailable (failed/empty). */
export const RESPONSE_DELAY_DEFAULT_MS = 600;

/** Grace window after a trailing-off fragment — the user is probably
 *  mid-thought; resumed speech cancels the pending reply. */
export const RESPONSE_DELAY_INCOMPLETE_MS = 1800;

/** Hold after a bare backchannel spoken into assistant silence
 *  ("Mm-hmm.", "Okay.") — an acknowledgement, not an answer. Live call
 *  ws_f9e79c03 (2026-07-27): the tester's thinking-noise "Mm-hmm."
 *  after an open question carried terminal punctuation, so it took the
 *  COMPLETE path and the speculative 650ms fire let the model treat it
 *  as the answer — three questions were skipped through in ~24s. Her
 *  real answers resumed 1.0-3.5s after such commits; resumed speech
 *  cancels the pending reply, so this hold costs nothing when the
 *  backchannel really was the whole turn (a genuinely terse "Yes."
 *  simply gets a slower reply). */
export const RESPONSE_DELAY_BACKCHANNEL_MS = 2500;

/**
 * Speculative replies are scheduled off the COMMIT, before words exist
 * (LUM-179). Latency forensics (18-turn tester call): waiting for
 * transcription (~434ms median) plus the post-transcript debounce put
 * stop→generation at ~940ms median — the words only matter for overlap
 * classification and echo detection, neither of which applies to a
 * lone utterance against a silent assistant.
 *
 * The delay is DURATION-AWARE (LUM-181, live call 14): commit→transcript
 * measures p50 414ms / p90 649ms, so a flat 300ms delay let ~70% of
 * post-hoc corrections lose the race — a 3.4s mumble transcribing to
 * two words got a reply 63ms before its fragment-grace correction
 * arrived, and the AI talked over the tester's answer window. Short
 * speech (< SUBSTANTIVE_UTTERANCE_MS) is mumble/fragment/cough
 * territory: it waits 650ms so corrections win at p90. Long speech is
 * a substantive answer where fragments are rare: it keeps the fast
 * 300ms path.
 */
export const RESPONSE_DELAY_SPECULATIVE_FAST_MS = 300;
export const RESPONSE_DELAY_SPECULATIVE_GUARDED_MS = 650;

/** Speech at least this long is a substantive answer, not a mumble. */
export const SUBSTANTIVE_UTTERANCE_MS = 4000;

/** Words that signal a deliberate interruption at any length.
 *  Sets, not Records: CONTINUATION_TAILS contains the word "then",
 *  and an object literal with a `then` property is a thenable —
 *  Sonar S7739 (reliability) rejects it, and awaiting one by accident
 *  would genuinely misbehave. */
const STOP_WORDS = new Set([
  'stop',
  'wait',
  'hold',
  'pause',
  'sorry',
  'hang',
  'question',
  'actually',
  'no',
]);

/** Trailing words that mean "I'm not done" even with punctuation. */
const CONTINUATION_TAILS = new Set([
  'and',
  'but',
  'so',
  'because',
  'or',
  'like',
  'also',
  'then',
  'plus',
  'um',
  'uh',
  'erm',
]);

/** Continuers and bare acknowledgements — the full utterance, not a
 *  tail. STT stamps terminal punctuation on hums ("Mm-hmm."), which
 *  otherwise reads as a COMPLETE sentence and earns the fast reply. */
const BACKCHANNEL_WORDS = new Set([
  'mm',
  'mmm',
  'mm-hmm',
  'mmhmm',
  'mhm',
  'hmm',
  'hm',
  'huh',
  'uh-huh',
  'uhhuh',
  'uh',
  'um',
  'erm',
  'er',
  'ah',
  'oh',
  'okay',
  'ok',
  'yeah',
  'yep',
  'yes',
  'yup',
  'ja',
  'right',
  'sure',
  'alright',
]);

/** Every word is a continuer/acknowledgement ("Mm-hmm.", "oh right",
 *  "okay yes"). Longer utterances are real turns even if built from
 *  these words. */
export function isBareBackchannel(transcript: string): boolean {
  const words = tokens(transcript);
  if (words.length === 0 || words.length > 3) return false;
  return words.every(w => BACKCHANNEL_WORDS.has(w));
}

export interface TurnDecision {
  /** Cancel the in-flight response (`response.cancel`). */
  cancel: boolean;
  /** Flush buffered output audio (`output_audio_buffer.clear`). */
  clearBuffer: boolean;
  /** Schedule the assistant's next turn (`response.create`) after
   *  `responseDelayMs`. NEVER send immediately — the delay is the
   *  cancellation window that keeps the AI from talking over resumed
   *  speech. */
  createResponse: boolean;
  /** Debounce for createResponse, chosen from utterance completeness. */
  responseDelayMs: number;
  /** User speech resumed: cancel any scheduled response.create. */
  cancelPendingResponse: boolean;
}

const NO_ACTION: TurnDecision = {
  cancel: false,
  clearBuffer: false,
  createResponse: false,
  responseDelayMs: 0,
  cancelPendingResponse: false,
};

function tokens(text: string): string[] {
  return text
    .toLowerCase()
    .split(/\s+/)
    .map(w => w.replace(/[^\p{L}\p{N}'-]/gu, ''))
    .filter(w => w.length > 0);
}

/** Deliberate interruption vs backchannel, for speech that overlapped
 *  assistant output. Content decides — STT renders hums as short
 *  multilingual fragments, so raw word counts under-classify. */
export function isInterruption(transcript: string): boolean {
  const words = tokens(transcript);
  if (words.length >= INTERRUPTION_MIN_WORDS) return true;
  return words.some(w => STOP_WORDS.has(w));
}

/** Reply delay from utterance completeness. Exported for tests. */
export function responseDelayFor(transcript: string): number {
  const trimmed = transcript.trim();
  if (trimmed.length === 0) return RESPONSE_DELAY_DEFAULT_MS;
  if (isBareBackchannel(trimmed)) return RESPONSE_DELAY_BACKCHANNEL_MS;
  const words = tokens(trimmed);
  const last = words.at(-1);
  if (last && CONTINUATION_TAILS.has(last)) {
    return RESPONSE_DELAY_INCOMPLETE_MS;
  }
  if (/[.!?…]$/.test(trimmed)) return RESPONSE_DELAY_COMPLETE_MS;
  // No terminal punctuation: the STT thinks the sentence isn't over.
  return RESPONSE_DELAY_INCOMPLETE_MS;
}

/**
 * STT prompt-echo detector (live call 6): on ambiguous audio the
 * transcription model can hallucinate its own vocabulary-bias prompt
 * back as user speech — the ENTIRE term list appeared twice as user
 * turns. Such an utterance is noise: it must not become a turn, an
 * interruption, a stored transcript line, or model context.
 *
 * Heuristic: 6+ words where >=60% of distinct tokens come from the
 * vocabulary term list. Real answers mention a couple of tools amid
 * normal words; only an echo is MADE of the list.
 */
export function isVocabularyEcho(
  transcript: string,
  vocabularyCsv: string,
): boolean {
  if (!vocabularyCsv) return false;
  const words = tokens(transcript);
  if (words.length < 6) return false;
  const vocab = new Set(tokens(vocabularyCsv.replaceAll(',', ' ')));
  const hits = words.filter(w => vocab.has(w)).length;
  return hits / words.length >= 0.6;
}

interface UtteranceContext {
  /** Was the assistant audibly speaking when this utterance started? */
  startedDuringSpeech: boolean;
  /** Response generating when this utterance started, if any. */
  interruptedResponseId: string | null;
}

export class RealtimeTurnArbiter {
  /** id of the response currently generating, if any. */
  private activeResponseId: string | null = null;
  /** Client-owned playback in progress. */
  private playbackActive = false;
  /** Context captured at speech_started for the utterance currently
   *  being spoken; moved into pendingByItem at commit. */
  private currentUtterance: UtteranceContext | null = null;
  /** Committed utterances awaiting transcription, keyed by item id.
   *  Rapid speech interleaves freely: a later utterance's
   *  speech_started must never shadow an earlier one's pending
   *  decision (live call 6: three consecutive "Stop." utterances were
   *  dropped by a single shared flag). */
  private pendingByItem = new Map<string, UtteranceContext>();
  /** A finished utterance's reply was suppressed because newer speech
   *  was live. If that newer speech is later discarded as an STT echo,
   *  the owed reply is re-issued (live call 11). Cleared the moment any
   *  response starts generating. */
  private replyDeferredToNewer = false;
  /** Item whose reply was scheduled speculatively at commit, before its
   *  words arrived (LUM-179); its transcription post-processes (echo →
   *  cancel, fragment → extend grace) instead of re-arbitrating. */
  private speculativeItemId: string | null = null;
  /** Clock time of the current utterance's speech_started, for the
   *  duration-aware speculative delay (LUM-181). */
  private speechStartedAtMs: number | null = null;

  /** `now` is injectable so duration behaviour is unit-testable. */
  constructor(private readonly now: () => number = Date.now) {}

  private speaking(): boolean {
    return this.activeResponseId !== null || this.playbackActive;
  }

  /** Feed every data-channel server event; execute the returned actions. */
  observe(event: Record<string, unknown>): TurnDecision {
    const type = event.type;
    if (typeof type !== 'string') return NO_ACTION;

    switch (type) {
      case 'response.created': {
        const response = event.response as { id?: unknown } | undefined;
        this.activeResponseId =
          typeof response?.id === 'string' ? response.id : 'unknown-response';
        // Whatever reply was owed is now being generated.
        this.replyDeferredToNewer = false;
        return NO_ACTION;
      }

      case 'response.done': {
        // Only the ACTIVE response's done may clear state — a late done
        // for an already-cancelled response must not make the arbiter
        // think generation stopped (Codex review finding 3).
        const response = event.response as { id?: unknown } | undefined;
        if (
          typeof response?.id !== 'string' ||
          response.id === this.activeResponseId
        ) {
          this.activeResponseId = null;
        }
        return NO_ACTION;
      }

      case 'output_audio_buffer.started':
        this.playbackActive = true;
        return NO_ACTION;

      case 'output_audio_buffer.stopped':
      case 'output_audio_buffer.cleared':
        this.playbackActive = false;
        return NO_ACTION;

      case 'input_audio_buffer.speech_started':
        this.currentUtterance = {
          startedDuringSpeech: this.speaking(),
          interruptedResponseId: this.activeResponseId,
        };
        this.speechStartedAtMs = this.now();
        // The user is talking again — a scheduled reply to their
        // previous utterance must not fire over them, and a speculative
        // reply loses its bet the same way: the words, when they land,
        // re-arbitrate through the normal deferral path. (Pending
        // interruption DECISIONS are unaffected: those act the moment
        // their transcription lands.)
        this.speculativeItemId = null;
        return { ...NO_ACTION, cancelPendingResponse: true };

      case 'input_audio_buffer.committed': {
        // Park this utterance's context under its item id until the
        // words arrive. Decisions are per-item — later speech cannot
        // shadow an earlier pending decision.
        const itemId =
          typeof event.item_id === 'string' ? event.item_id : 'unknown-item';
        this.pendingByItem.set(
          itemId,
          this.currentUtterance ?? {
            startedDuringSpeech: this.speaking(),
            interruptedResponseId: this.activeResponseId,
          },
        );
        this.currentUtterance = null;
        const durationMs =
          this.speechStartedAtMs !== null
            ? this.now() - this.speechStartedAtMs
            : null;
        this.speechStartedAtMs = null;
        // Bounded: never-transcribed items must not leak.
        if (this.pendingByItem.size > 16) {
          const oldest = this.pendingByItem.keys().next().value;
          if (oldest !== undefined) this.pendingByItem.delete(oldest);
        }
        // Speculative reply (LUM-179): a lone utterance against a
        // silent assistant gets a reply regardless of its words — don't
        // wait for them. Echoes and fragments are corrected when the
        // transcript lands; resumed speech cancels like any reply.
        // Unknown duration (missed speech_started) takes the guarded
        // path: protection over speed.
        if (!this.speaking() && this.pendingByItem.size === 1) {
          this.speculativeItemId = itemId;
          const substantive =
            durationMs !== null && durationMs >= SUBSTANTIVE_UTTERANCE_MS;
          return {
            ...NO_ACTION,
            createResponse: true,
            responseDelayMs: substantive
              ? RESPONSE_DELAY_SPECULATIVE_FAST_MS
              : RESPONSE_DELAY_SPECULATIVE_GUARDED_MS,
          };
        }
        return NO_ACTION;
      }

      case 'conversation.item.input_audio_transcription.failed': {
        const itemId =
          typeof event.item_id === 'string' ? event.item_id : 'unknown-item';
        const ctx = this.takePending(event);
        if (!ctx) return NO_ACTION;
        if (itemId === this.speculativeItemId) {
          // A speculative reply is already scheduled/in flight; there
          // are no words to correct it with — let it ride.
          this.speculativeItemId = null;
          return NO_ACTION;
        }
        // No words will come. If the assistant is silent and the user
        // isn't already speaking again, respond after the default delay
        // rather than stalling the conversation.
        if (
          this.speaking() ||
          this.currentUtterance !== null ||
          this.pendingByItem.size > 0
        ) {
          return NO_ACTION;
        }
        return {
          ...NO_ACTION,
          createResponse: true,
          responseDelayMs: RESPONSE_DELAY_DEFAULT_MS,
        };
      }

      case 'conversation.item.input_audio_transcription.completed': {
        const itemId =
          typeof event.item_id === 'string' ? event.item_id : 'unknown-item';
        const ctx = this.takePending(event);
        if (!ctx) return NO_ACTION;
        const transcript =
          typeof event.transcript === 'string' ? event.transcript : '';

        if (itemId === this.speculativeItemId) {
          // Post-hoc correction of a speculative reply (LUM-179). The
          // echo case is handled by the caller BEFORE arbitration
          // (discardPending). Two corrections remain: a trailing-off
          // fragment (STT thinks the sentence isn't over) and a bare
          // backchannel (an acknowledgement, not an answer — live call
          // ws_f9e79c03). Both swap the in-flight guarded schedule for
          // their longer grace — unless the reply already started
          // generating (response.created seen), where cutting in would
          // be worse than replying.
          this.speculativeItemId = null;
          const corrected = responseDelayFor(transcript);
          if (
            corrected > RESPONSE_DELAY_SPECULATIVE_GUARDED_MS &&
            !this.speaking()
          ) {
            return {
              ...NO_ACTION,
              cancelPendingResponse: true,
              createResponse: true,
              responseDelayMs: corrected,
            };
          }
          return NO_ACTION;
        }
        // Transcription lags speech: by the time this utterance's words
        // arrive the user may already be mid-NEXT-utterance. A reply
        // always belongs to the newest thing said — never schedule one
        // while newer speech is live (this re-fixes, correctly, what
        // the old shared-flag race used to mask by dropping decisions).
        // Every such deferral is REMEMBERED: if the newer speech turns
        // out to be a dropped STT echo, discardPending() re-arbitrates
        // the owed reply (live call 11: a real answer's reply was
        // deferred to phantom echoes and only arrived when the user
        // gave up and answered again).
        const userSpeaking =
          this.currentUtterance !== null || this.pendingByItem.size > 0;

        if (ctx.startedDuringSpeech && !isInterruption(transcript)) {
          // Backchannel over assistant speech: nothing at all. But if
          // the assistant has since finished, the utterance was really
          // a terse ANSWER ("Mhm." to "ready to start?") — reply.
          if (this.speaking()) return NO_ACTION;
          if (userSpeaking) {
            this.replyDeferredToNewer = true;
            return NO_ACTION;
          }
          return {
            ...NO_ACTION,
            createResponse: true,
            responseDelayMs: responseDelayFor(transcript),
          };
        }

        if (ctx.startedDuringSpeech || this.speaking()) {
          // Deliberate interruption: stop whatever of the interrupted
          // output still lives. Reply only if the user isn't already
          // saying the next thing — that utterance owns the reply.
          const cancel =
            ctx.interruptedResponseId !== null &&
            ctx.interruptedResponseId === this.activeResponseId;
          const clearBuffer = this.playbackActive;
          if (cancel) this.activeResponseId = null;
          this.playbackActive = false;
          if (userSpeaking) this.replyDeferredToNewer = true;
          return {
            cancel,
            clearBuffer,
            createResponse: !userSpeaking,
            responseDelayMs: RESPONSE_DELAY_COMPLETE_MS,
            cancelPendingResponse: false,
          };
        }

        // Plain conversation turn while the assistant is silent.
        if (userSpeaking) {
          this.replyDeferredToNewer = true;
          return NO_ACTION;
        }
        return {
          ...NO_ACTION,
          createResponse: true,
          responseDelayMs: responseDelayFor(transcript),
        };
      }

      default:
        return NO_ACTION;
    }
  }

  /**
   * Release an item's pending slot WITHOUT arbitration — for utterances
   * the caller discarded before the words reached turn policy (STT
   * prompt echoes). Live call 9: a dropped echo's stuck entry made
   * `userSpeaking` permanently true and silently suppressed every later
   * reply — the interview went dead.
   *
   * Returns a decision because the drop can settle an OWED reply (live
   * call 11): when an earlier utterance's reply was deferred to this
   * now-phantom "newer speech" and nothing else is live, the deferral
   * would otherwise dangle until the user re-answered into the silence.
   */
  discardPending(itemId: string): TurnDecision {
    this.pendingByItem.delete(itemId);
    if (itemId === this.speculativeItemId) {
      // The speculative bet was placed on an STT echo (LUM-179): kill
      // the scheduled create if it hasn't fired, cancel the response if
      // it has, and silence any audio that raced out.
      this.speculativeItemId = null;
      const cancel = this.activeResponseId !== null;
      const clearBuffer = this.playbackActive;
      if (cancel) this.activeResponseId = null;
      this.playbackActive = false;
      return {
        cancel,
        clearBuffer,
        createResponse: false,
        responseDelayMs: 0,
        cancelPendingResponse: true,
      };
    }
    if (
      this.replyDeferredToNewer &&
      this.pendingByItem.size === 0 &&
      this.currentUtterance === null &&
      !this.speaking()
    ) {
      this.replyDeferredToNewer = false;
      return {
        ...NO_ACTION,
        createResponse: true,
        responseDelayMs: RESPONSE_DELAY_DEFAULT_MS,
      };
    }
    return NO_ACTION;
  }

  private takePending(event: Record<string, unknown>): UtteranceContext | null {
    const itemId =
      typeof event.item_id === 'string' ? event.item_id : 'unknown-item';
    const ctx = this.pendingByItem.get(itemId);
    if (ctx) {
      this.pendingByItem.delete(itemId);
      return ctx;
    }
    return null;
  }
}
